import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'

// Privacy-first cookie consent. Google Consent Mode defaults to "denied" (set in
// index.html), so no non-essential cookies are set until the user accepts. The
// choice is remembered in localStorage. Bilingual via useLocale.
const KEY = 'rs-consent'

type Consent = 'granted' | 'denied'

function applyConsent(state: Consent) {
  const g = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof g === 'function') {
    g('consent', 'update', {
      ad_storage: state === 'granted' ? 'granted' : 'denied',
      ad_user_data: state === 'granted' ? 'granted' : 'denied',
      ad_personalization: state === 'granted' ? 'granted' : 'denied',
      analytics_storage: state === 'granted' ? 'granted' : 'denied',
    })
  }
}

export default function CookieConsent() {
  const locale = useLocale()
  const tr = (k: string) => t(locale, k)
  const [show, setShow] = useState(false)

  useEffect(() => {
    let saved: string | null = null
    try { saved = localStorage.getItem(KEY) } catch { /* private mode */ }
    if (saved === 'granted') {
      applyConsent('granted') // re-affirm a previously granted choice on load
    } else if (saved !== 'denied') {
      setShow(true) // no choice yet — ask (defaults stay denied meanwhile)
    }
    // Footer "Cookie settings" link reopens the banner so users can change their choice.
    const reopen = () => setShow(true)
    window.addEventListener('rs-open-consent', reopen)
    return () => window.removeEventListener('rs-open-consent', reopen)
  }, [])

  const choose = (state: Consent) => {
    try { localStorage.setItem(KEY, state) } catch { /* ignore */ }
    applyConsent(state)
    setShow(false)
  }

  if (!show) return null

  return (
    <div
      role="dialog"
      aria-label={tr('consent.aria')}
      className="no-print fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface/98 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center">
        <p className="flex-1 text-sm text-text-body">
          {tr('consent.message')}{' '}
          <Link to={localizedHref('/privacy/', locale)} className="text-brand-text underline">
            {tr('consent.privacy')}
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => choose('denied')}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text-body hover:bg-surface-2"
          >
            {tr('consent.decline')}
          </button>
          <button
            onClick={() => choose('granted')}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-on-primary hover:opacity-90"
          >
            {tr('consent.accept')}
          </button>
        </div>
      </div>
    </div>
  )
}
